import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { rulesAPI } from '../services/api';

interface RuleDefinition {
  key: string;
  name: string;
  description: string;
  category: string;
  value_type: string;
  default_value: string;
  scope: string;
  is_active: boolean;
}

interface GlobalRuleEntry extends RuleDefinition {
  value: string;
}

const categoryLabels: Record<string, string> = {
  presence: 'Presenze',
  leave: 'Ferie',
  export: 'Export',
  communication: 'Comunicazioni',
  visibility: 'Area operatore',
};

const RulesCatalog: React.FC = () => {
  const [rules, setRules] = useState<RuleDefinition[]>([]);
  const [globalRules, setGlobalRules] = useState<GlobalRuleEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [catalogRes, globalRes] = await Promise.all([
        rulesAPI.getCatalog(true),
        rulesAPI.getGlobalRules(),
      ]);
      setRules(catalogRes.rules || []);
      setGlobalRules(globalRes.rules || []);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore caricamento catalogo');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleGlobalToggle = async (key: string, enabled: boolean) => {
    setSaving(key);
    try {
      await rulesAPI.setGlobalRule(key, enabled ? 'true' : 'false');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Errore salvataggio regola globale');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  const groupRules = rules.filter((r) => r.scope === 'group');
  const categories = [...new Set(groupRules.map((r) => r.category))];

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" gutterBottom>
        Regole globali
      </Typography>
      <Paper sx={{ p: 2, mb: 3 }}>
        {globalRules.map((rule) => (
          <Box key={rule.key} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={rule.value === 'true'}
                  onChange={(e) => handleGlobalToggle(rule.key, e.target.checked)}
                  disabled={saving === rule.key}
                />
              }
              label={rule.name}
            />
            <Typography variant="body2" color="text.secondary">
              {rule.description}
            </Typography>
          </Box>
        ))}
      </Paper>

      <Typography variant="h6" gutterBottom>
        Catalogo regole per gruppo
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Le regole di categoria &quot;Area operatore&quot; sono documentate per uso futuro. Oggi la visibilità
        nell&apos;area riservata è derivata automaticamente dalle regole Presenze, Ferie ed Export.
      </Alert>

      {categories.map((category) => (
        <Box key={category} sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {categoryLabels[category] || category}
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Regola</TableCell>
                  <TableCell>Descrizione</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Default</TableCell>
                  <TableCell>Stato</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupRules
                  .filter((r) => r.category === category)
                  .map((rule) => (
                    <TableRow key={rule.key}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {rule.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rule.key}
                        </Typography>
                      </TableCell>
                      <TableCell>{rule.description}</TableCell>
                      <TableCell>{rule.value_type}</TableCell>
                      <TableCell>{rule.default_value}</TableCell>
                      <TableCell>
                        <Chip
                          label={rule.is_active ? 'Attiva' : 'Solo documentazione'}
                          size="small"
                          color={rule.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default RulesCatalog;
